import { useEffect, useState } from 'react';
import { Phone, Delete, Copy, Check, ArrowRight, Grid3x3, ChevronUp } from 'lucide-react';
import { useSoftphone } from '@/store/softphone';
import clsx from 'clsx';

const DIALPAD: { key: string; sub?: string }[][] = [
  [{ key: '1' }, { key: '2', sub: 'ABC' }, { key: '3', sub: 'DEF' }],
  [{ key: '4', sub: 'GHI' }, { key: '5', sub: 'JKL' }, { key: '6', sub: 'MNO' }],
  [{ key: '7', sub: 'PQRS' }, { key: '8', sub: 'TUV' }, { key: '9', sub: 'WXYZ' }],
  [{ key: '*' }, { key: '0', sub: '+' }, { key: '#' }],
];

// Neumorphic key — compact, soft raised, depresses on press
const KEY_BASE =
  'h-12 rounded-xl bg-surface text-slate-800 flex flex-col items-center justify-center select-none ' +
  'shadow-[1px_2px_4px_rgba(15,23,42,0.06),-1px_-1px_3px_rgba(255,255,255,0.9)] ' +
  'active:shadow-[inset_1px_2px_4px_rgba(15,23,42,0.08),inset_-1px_-1px_2px_rgba(255,255,255,0.5)] ' +
  'active:bg-slate-50 active:scale-[0.97] transition-all duration-100';

const PAD_PREF_KEY = 'ccark.dialpadOpen';

export function SoftphoneWidget() {
  const { state, init, dial, dtmf } = useSoftphone();
  const [number, setNumber] = useState('');
  const [copied, setCopied] = useState(false);
  const [padOpen, setPadOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return localStorage.getItem(PAD_PREF_KEY) !== '0';
  });

  useEffect(() => { init().catch(console.error); }, [init]);
  useEffect(() => {
    try { localStorage.setItem(PAD_PREF_KEY, padOpen ? '1' : '0'); } catch { /* */ }
  }, [padOpen]);

  const isActive = state === 'in_call' || state === 'ringing' || state === 'calling';

  // Keyboard support
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const ae = document.activeElement;
      const tag = ae?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (ae as HTMLElement)?.isContentEditable) return;
      if (e.key === 'Backspace') {
        if (!isActive) setNumber((n) => n.slice(0, -1));
        return;
      }
      if (e.key === 'Enter') {
        if (state === 'registered' && number.trim()) dial(number.trim());
        return;
      }
      if (/^[0-9*#]$/.test(e.key)) {
        if (state === 'in_call') dtmf(e.key);
        else setNumber((n) => n + e.key);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [state, number, dial, dtmf, isActive]);

  const onCall = () => { if (number.trim()) dial(number.trim()); };
  const onKey = (k: string) => setNumber((n) => n + k);
  const copy = async () => {
    if (!number) return;
    try {
      await navigator.clipboard.writeText(number);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* */ }
  };

  return (
      <div className="w-full rounded-3xl bg-surface ring-1 ring-black/5 shadow-[0_2px_24px_-6px_rgba(15,23,42,0.08)] overflow-hidden">
        {isActive ? (
          <ActiveHint />
        ) : (
          <div className="bg-slate-50/60 p-3 space-y-2.5">
            {/* Number display */}
            <div className="flex items-center gap-1 bg-surface rounded-2xl ring-1 ring-slate-200/70 px-3 py-2 shadow-[inset_1px_1px_3px_rgba(15,23,42,0.04)]">
              <input
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="Numara"
                className="flex-1 min-w-0 bg-transparent text-lg font-mono tracking-wider text-slate-800 placeholder:text-slate-300 placeholder:font-sans placeholder:text-sm focus:outline-none"
                onKeyDown={(e) => e.key === 'Enter' && state === 'registered' && onCall()}
              />
              <button
                onClick={() => setPadOpen((v) => !v)}
                className={clsx(
                  'p-1.5 rounded-lg transition',
                  padOpen ? 'text-blue-600 bg-blue-50' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-50',
                )}
                title={padOpen ? 'Tuş panelini gizle' : 'Tuş panelini göster'}
              >
                {padOpen ? <ChevronUp size={14} /> : <Grid3x3 size={14} />}
              </button>
              <button
                onClick={copy}
                disabled={!number}
                className="p-1.5 text-slate-400 hover:text-blue-600 disabled:opacity-25 rounded-lg transition"
                title="Kopyala"
              >
                {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
              </button>
              <button
                onClick={() => setNumber((n) => n.slice(0, -1))}
                disabled={!number}
                className="p-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-25 rounded-lg transition"
                title="Sil"
              >
                <Delete size={14} />
              </button>
            </div>

            {/* Dialpad — toggleable */}
            {padOpen && (
              <div className="grid grid-cols-3 gap-1.5">
                {DIALPAD.flat().map(({ key, sub }) => (
                  <button
                    key={key}
                    onClick={() => onKey(key)}
                    className={KEY_BASE}
                  >
                    <span className="text-[18px] leading-none font-medium">{key}</span>
                    {sub && <span className="text-[8px] text-slate-400 mt-0.5 tracking-[0.15em] leading-none">{sub}</span>}
                  </button>
                ))}
              </div>
            )}

            {/* Call button */}
            <button
              onClick={onCall}
              disabled={state !== 'registered' || !number.trim()}
              className={clsx(
                'w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl text-[14px] font-semibold transition-all active:scale-[0.98]',
                state === 'registered' && number.trim()
                  ? 'bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-[#08140d] shadow-[0_4px_12px_-3px_rgba(16,185,129,0.5)]'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed',
              )}
            >
              <Phone size={15} fill={state === 'registered' && number.trim() ? 'currentColor' : 'none'} />
              Ara
            </button>
          </div>
        )}
      </div>
  );
}

function ActiveHint() {
  const { call, state } = useSoftphone();
  if (!call) return null;
  return (
    <div className="p-3.5 flex items-center gap-2.5">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center font-bold text-xs flex-shrink-0 shadow-[0_3px_8px_-2px_rgba(59,130,246,0.4)]">
        {(call.displayName || call.peer || '?').slice(0, 2).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900 truncate tracking-tight">
          {call.displayName || call.peer}
        </div>
        <div className="text-xs text-slate-500 font-mono truncate">
          {state === 'in_call' ? '● Görüşme aktif' : state === 'ringing' ? '◐ Çalıyor' : '◐ Aranıyor'}
        </div>
      </div>
      <ArrowRight size={14} className="text-slate-400" />
    </div>
  );
}
